// src/pages/Caja.jsx
import React, { useEffect, useState } from "react";
import "./Caja.css";
import Toast from "../components/Toast";
import { authFetch } from "../utils/authFetch";
import { Boton, EncabezadoPagina, Iconos, Insignia } from "../componentes/base";

/* =========================
   Hook: detectar móvil
========================= */
function useIsMobile(breakpoint = 560) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpoint]);

  return isMobile;
}

/* La clave lleva el origen porque el día se arma con cuatro tablas y cada
   una numera por su cuenta: el movimiento 5 de `caja` y el 5 de
   `caja_wifi` son distintos, y con la clave repetida React mezcla filas. */
const clave = (r) => `${r.origen}:${r.id}`;

/* =========================
   Cards móvil: Instalaciones
========================= */
function InstalacionesCards({ rows = [] }) {
  if (!rows.length) {
    return <div className="caja-empty">Sin movimientos de instalación en esta fecha</div>;
  }

  return (
    <div className="caja-cards">
      {rows.map((m) => (
        <div className="caja-carditem" key={clave(m)}>
          <div className="caja-carditem-top">
            <div className="caja-carditem-title">{m.concepto || "Instalación"}</div>
            <div className="caja-carditem-amount">${Number(m.monto || 0).toFixed(2)}</div>
          </div>

          <div className="caja-carditem-grid">
            <div className="caja-kv">
              <span className="caja-k">Forma pago</span>
              <span className="caja-v">{m.forma_pago || "—"}</span>
            </div>

            <div className="caja-kv">
              <span className="caja-k">Hora</span>
              <span className="caja-v">{m.hora || "—"}</span>
            </div>

            <div className="caja-kv caja-kv-full">
              <span className="caja-k">Referencia</span>
              <span className="caja-v">{m.referencia || "—"}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================
   Cards móvil: Mensualidades
========================= */
function MensualidadesCards({ rows = [] }) {
  if (!rows.length) {
    return <div className="caja-empty">Sin pagos de mensualidad en esta fecha</div>;
  }

  return (
    <div className="caja-cards">
      {rows.map((p) => (
        <div className="caja-carditem" key={clave(p)}>
          <div className="caja-carditem-top">
            <div className="caja-carditem-title">Cliente {p.cliente_id}</div>
            <div className="caja-carditem-amount">${Number(p.monto || 0).toFixed(2)}</div>
          </div>

          <div className="caja-carditem-grid">
            <div className="caja-kv">
              <span className="caja-k">Forma pago</span>
              <span className="caja-v">{p.forma_pago || "—"}</span>
            </div>

            <div className="caja-kv">
              <span className="caja-k">Hora</span>
              <span className="caja-v">{p.hora || "—"}</span>
            </div>

            <div className="caja-kv caja-kv-full">
              <span className="caja-k">Referencia</span>
              <span className="caja-v">{p.referencia || "—"}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function hoyLocalYYYYMMDD() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}

const hoy = hoyLocalYYYYMMDD();

export default function Caja() {

  const isMobile = useIsMobile(560);

  const [fecha, setFecha] = useState(hoy);
  const [instalaciones, setInstalaciones] = useState([]);
  const [mensualidades, setMensualidades] = useState([]);
  const [totales, setTotales] = useState({
    instalaciones: 0,
    oficina: 0,
    bancomer: 0,
    banamex: 0,
    otros: 0,
    gran_total: 0,
    total_dia: 0,
    desglose: {
      pagos: { oficina: 0, bancomer: 0, banamex: 0, otros: 0 },
      caja: { oficina: 0, bancomer: 0, banamex: 0, otros: 0 },
    },
  });

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // ✅ NUEVO: toggle de desglose
  const [showDesglose, setShowDesglose] = useState(false);

  const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;

  async function cargarMovimientos(fechaStr) {
    try {
      setLoading(true);
      const res = await authFetch(`/api/caja/movimientos?fecha=${fechaStr}`);
      const data = await res.json();

      if (data.ok) {
        setInstalaciones(data.instalaciones || []);
        setMensualidades(data.mensualidades || []);
        setTotales(
          data.totales || {
            instalaciones: 0,
            oficina: 0,
            bancomer: 0,
            banamex: 0,
            otros: 0,
            gran_total: 0,
            total_dia: 0,
            desglose: {
              pagos: { oficina: 0, bancomer: 0, banamex: 0, otros: 0 },
              caja: { oficina: 0, bancomer: 0, banamex: 0, otros: 0 },
            },
          }
        );
      } else {
        setToast({
          mensaje: data.error || data.mensaje || "Error cargando movimientos",
          tipo: "error",
        });
      }
    } catch (e) {
      console.log("ERROR caja:", e);
      setToast({ mensaje: "Error de red al cargar caja", tipo: "error" });
    } finally {
      setLoading(false);
    }
  }

  // ✅ sigue tu carga inicial (si luego quieres quitarla, lo hacemos aparte)
  useEffect(() => {
    //cargarMovimientos(fecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleFechaChange(e) {
    const nuevaFecha = e.target.value;
    setFecha(nuevaFecha);
    cargarMovimientos(nuevaFecha);
    setShowDesglose(false);
  }

  const pagosDesg = totales?.desglose?.pagos || {};
  const cajaDesg = totales?.desglose?.caja || {};

  return (
    <div className="caja-page">
      <EncabezadoPagina
        icono={Iconos.caja}
        titulo="Caja"
        resumen={`${fmtMoney(totales.total_dia)} · ${instalaciones.length + mensualidades.length} movimientos`}
        acciones={
          <Boton
            variante="secundario"
            icono={Iconos.actualizar}
            onClick={() => cargarMovimientos(fecha)}
          >
            Actualizar
          </Boton>
        }
      />

      {/* Barra superior: fecha */}
      <div className="caja-toolbar">
        <div className="caja-date-card">
          <label>Fecha</label>
          <input type="date" value={fecha} onChange={handleFechaChange} />
        </div>
      </div>

      {/* Resumen */}
      <div className="caja-summary-grid">
        <div className="summary-card total">
          <span className="summary-label">Total del día</span>
          <span className="summary-amount">{fmtMoney(totales.total_dia)}</span>

          {/* ✅ NUEVO: botón “Ver desglose” */}
          <Boton
            type="button"
            variante="secundario"
            icono={showDesglose ? Iconos.desplegar : Iconos.siguiente}
            onClick={() => setShowDesglose((s) => !s)}
          >
            {showDesglose ? "Ocultar desglose" : "Ver desglose"}
          </Boton>
        </div>

        <div className="summary-card instalaciones">
          <span className="summary-label">Instalaciones</span>
          <span className="summary-amount">{fmtMoney(totales.instalaciones)}</span>
        </div>

        <div className="summary-card oficina">
          <span className="summary-label">Oficina</span>
          <span className="summary-amount">{fmtMoney(totales.oficina)}</span>
        </div>

        <div className="summary-card bancomer">
          <span className="summary-label">Bancomer</span>
          <span className="summary-amount">{fmtMoney(totales.bancomer)}</span>
        </div>

        <div className="summary-card banamex">
          <span className="summary-label">Banamex</span>
          <span className="summary-amount">{fmtMoney(totales.banamex)}</span>
        </div>

        <div className="summary-card otros">
          <span className="summary-label">Otros</span>
          <span className="summary-amount">{fmtMoney(totales.otros)}</span>
        </div>
      </div>

      {/* ✅ NUEVO: panel colapsable del desglose */}
      {showDesglose && (
        <div className="caja-desglose-panel">
          <div className="caja-desglose-col">
            <div className="caja-desglose-title">Pagos (mensualidades)</div>
            <div className="caja-desglose-row">
              <span>Oficina</span><b>{fmtMoney(pagosDesg.oficina)}</b>
            </div>
            <div className="caja-desglose-row">
              <span>Bancomer</span><b>{fmtMoney(pagosDesg.bancomer)}</b>
            </div>
            <div className="caja-desglose-row">
              <span>Banamex</span><b>{fmtMoney(pagosDesg.banamex)}</b>
            </div>
            <div className="caja-desglose-row">
              <span>Otros</span><b>{fmtMoney(pagosDesg.otros)}</b>
            </div>
          </div>

          <div className="caja-desglose-col">
            <div className="caja-desglose-title">Caja (instalación / extras)</div>
            <div className="caja-desglose-row">
              <span>Oficina</span><b>{fmtMoney(cajaDesg.oficina)}</b>
            </div>
            <div className="caja-desglose-row">
              <span>Bancomer</span><b>{fmtMoney(cajaDesg.bancomer)}</b>
            </div>
            <div className="caja-desglose-row">
              <span>Banamex</span><b>{fmtMoney(cajaDesg.banamex)}</b>
            </div>
            <div className="caja-desglose-row">
              <span>Otros</span><b>{fmtMoney(cajaDesg.otros)}</b>
            </div>
          </div>
        </div>
      )}

      {/* Cuerpo: tablas o cards */}
      <div className="caja-tables">
        {/* INSTALACIONES */}
        <div className="caja-card">
          <div className="caja-card-header">
            <h2>
              <Iconos.equipos size={17} strokeWidth={1.8} />
              Instalaciones
            </h2>
            <Insignia variante="neutra" texto={`${instalaciones.length} mov.`} />
          </div>

          {isMobile ? (
            <InstalacionesCards rows={instalaciones} />
          ) : (
            <div className="caja-table-wrapper">
              <table className="caja-table conceptos">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>Forma pago</th>
                    <th>Referencia</th>
                    <th>Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {instalaciones.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="caja-empty">
                        Sin movimientos de instalación en esta fecha
                      </td>
                    </tr>
                  ) : (
                    instalaciones.map((m) => (
                      <tr key={clave(m)}>
                        <td>{m.concepto}</td>
                        <td>{fmtMoney(m.monto)}</td>
                        <td>{m.forma_pago}</td>
                        <td>{m.referencia}</td>
                        <td>{m.hora}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* MENSUALIDADES */}
        <div className="caja-card">
          <div className="caja-card-header">
            <h2>
              <Iconos.pagos size={17} strokeWidth={1.8} />
              Mensualidades
            </h2>
            <Insignia variante="neutra" texto={`${mensualidades.length} mov.`} />
          </div>

          {isMobile ? (
            <MensualidadesCards rows={mensualidades} />
          ) : (
            <div className="caja-table-wrapper">
              <table className="caja-table clientes">
                <thead>
                  <tr>
                    <th>Cliente ID</th>
                    <th>Monto</th>
                    <th>Forma pago</th>
                    <th>Referencia</th>
                    <th>Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {mensualidades.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="caja-empty">
                        Sin pagos de mensualidad en esta fecha
                      </td>
                    </tr>
                  ) : (
                    mensualidades.map((p) => (
                      <tr key={clave(p)}>
                        <td>{p.cliente_id}</td>
                        <td>{fmtMoney(p.monto)}</td>
                        <td>{p.forma_pago}</td>
                        <td>{p.referencia}</td>
                        <td>{p.hora}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Loader */}
      {loading && (
        <div className="caja-loading-overlay">
          <div className="caja-loading-card">
            <div className="caja-spinner" />
            <p>Cargando movimientos...</p>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast mensaje={toast.mensaje} tipo={toast.tipo} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
